import { useCallback, useEffect, useState } from "react";
import type { AssignableOwnerDto, ProposalDeadlineRadarItem, TripDto } from "@tc/shared";
import { opsApi } from "@/features/ops/api";
import { tripsApi } from "@/features/trips/api";
import { useClubFilter } from "@/lib/clubs/club-filter";

interface OpsWorklist {
  trips: TripDto[];
  owners: AssignableOwnerDto[];
  /** Cross-club pending proposal deadlines for the radar + at-risk signal (TW-3446). */
  radar: ProposalDeadlineRadarItem[];
  /** Resolve an owner id to a display name (null when unassigned / unknown). */
  ownerName: (ownerUserId: string | null) => string | null;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
}

/**
 * Loads the back-office worklist: the cross-club trip list (scoped by the topbar club switcher
 * via `?clubId=`) plus the assignable-owner pick-list, refetched whenever the selected club
 * changes (TW-3440). Shared by the ops dashboard, requests inbox and all-trips table so every
 * surface reflects the same club scope and resolves owner names the same way. Plain fetch +
 * local state, mirroring the rest of the web app (no TanStack yet).
 */
export function useOpsWorklist(): OpsWorklist {
  const { scopeId } = useClubFilter();
  const [trips, setTrips] = useState<TripDto[]>([]);
  const [owners, setOwners] = useState<AssignableOwnerDto[]>([]);
  const [radar, setRadar] = useState<ProposalDeadlineRadarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [tripList, ownerList, radarList] = await Promise.all([
        tripsApi.list(scopeId),
        tripsApi.assignableOwners(),
        opsApi.deadlineRadar(scopeId),
      ]);
      setTrips(tripList);
      setOwners(ownerList);
      setRadar(radarList);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const ownerName = useCallback(
    (ownerUserId: string | null) =>
      ownerUserId ? (owners.find((o) => o.id === ownerUserId)?.name ?? null) : null,
    [owners],
  );

  return { trips, owners, radar, ownerName, loading, error, reload };
}
